import "core-js/modules/es.string.trim";
import "core-js/modules/es.string.trim";
/**
 * Component: Cart add-on script
 * ------------------------------------------------------------------------------
 * Loosely coupled scripts for the Cart add-on functionality.
 *
 * @namespace cartAddOn
 */

import * as cart from '@shopify/theme-cart';
import axios from 'axios';
import cssClasses from '~/helpers/cssClasses';
import { debounce, on } from '~/helpers/utils';
/**
 * DOM selectors.
 */

const selectors = {
  container: '[js-cart-add-on="container"]',
  checkbox: '[js-cart-add-on="checkbox"]',
  label: '[js-cart-add-on="label"]',
  message: '[js-cart-add-on="message"]',
  textarea: '[js-cart-add-on="textarea"]'
};
/**
 * Create a new cart add-on object.
 */

export default (() => {
  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selectors.container),
    checkbox: document.querySelector(selectors.checkbox),
    label: document.querySelector(selectors.label),
    message: document.querySelector(selectors.message),
    textarea: document.querySelector(selectors.textarea)
  };
  /**
   * Initialise component.
   */

  function init() {
    setEventBusListeners();
    setEventListeners();
  }
  /**
   * Set eventBus listeners.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen('CartTable:rendered', response => setInitState(response));
  }
  /**
   * Set event listeners.
   */


  function setEventListeners() {
    on('click', nodeSelectors.label, () => handleAddOnToggle());

    if (theme.features.cartAddOnMessage) {
      on('keyup', nodeSelectors.message, debounce(handleMessageUpdate, 500));
    }
  }
  /**
   * Set initial state of add-on form.
   * @param {Object} data - Cart.js data.
   */


  function setInitState(data) {
    if (isAddOnInCart(data)) {
      setActiveState();
      return;
    }

    resetActiveState();
  }
  /**
   * Check if add-on product is already in cart.
   * @param {Object} data - Cart.js data.
   */


  function isAddOnInCart(data) {
    if (isOnlyItemInCart(data)) {
      return false;
    }

    return data.items.some(item => {
      return item.product_type.toLowerCase() === 'add-on';
    });
  }
  /**
   * Check if the only item in cart is an add-on.
   * @param {Object} data - Cart.js data.
   * @returns {Boolean}
   */


  function isOnlyItemInCart(data) {
    const totalNumberOfItems = data.items.reduce((total, item) => {
      return item.product_type.toLowerCase() === 'add-on' ? 0 : total + item.quantity;
    }, 0);

    if (totalNumberOfItems === 0 && data.item_count > 0) {
      Frame.EventBus.emit('AjaxCart:clearCart');
    }

    return totalNumberOfItems === 0;
  }
  /**
   * Set add-on state to active.
   */


  function setActiveState() {
    nodeSelectors.container.setAttribute('data-active', true);
    nodeSelectors.checkbox.checked = true;

    if (theme.features.cartAddOnMessage) {
      nodeSelectors.message.classList.add(cssClasses.active);
    }
  }
  /**
   * Reset add-on state to inactive.
   */


  function resetActiveState() {
    nodeSelectors.container.setAttribute('data-active', false);
    nodeSelectors.checkbox.checked = false;

    if (theme.features.cartAddOnMessage) {
      nodeSelectors.message.classList.remove(cssClasses.active);
      nodeSelectors.textarea.value = '';
    }
  }
  /**
   * Handle add on toggle events.
   * - Add/Remove existing add-on product in cart.
   * - Show/hide message if enabled in theme settings.
   */


  function handleAddOnToggle() {
    Frame.EventBus.emit('CartTable:updating');

    if (!isAddOnActive()) {
      addAddOn();
      return;
    }

    findAndRemoveAddOn();
  }
  /**
   * Checks to see current state of add-on.
   */


  function isAddOnActive() {
    return nodeSelectors.container.getAttribute('data-active') === 'true';
  }
  /**
   * Add add-on product from theme settings to cart with validation.
   */


  function addAddOn() {
    const id = Number(nodeSelectors.container.dataset.variantId);
    cart.addItem(id, {
      qty: 1
    }).then(() => {
      setActiveState();
      Frame.EventBus.emit('AjaxCart:addOnItemAdded');
    }).catch(error => {
      throw new Error('Could not add item to cart. Check product inventory.', error);
    });
  }
  /**
   * Find and remove add-on product from cart.
   */


  function findAndRemoveAddOn() {
    axios.get("/cart.js?no-cache=".concat(new Date().getTime())).then(response => {
      const product = response.data.items.find(item => {
        return item.product_type.toLowerCase() === 'add-on';
      });
      removeAddOn(product);
    }).catch(error => {
      throw new Error('Could not find ', error);
    });
  }
  /**
   * Find and remove add-on product from cart.
   * @param {Object} product - Add-on product object from cart.js.
   */


  function removeAddOn(product) {
    cart.removeItem(product.key).then(() => {
      resetActiveState();
      cart.clearNote();
      Frame.EventBus.emit('AjaxCart:addOnItemRemoved');
    }).catch(error => {
      throw new Error('Could not remove add-on product from cart', error);
    });
  }
  /**
   * Handle text inputs on the add-on message textarea element.
   * @param {Object} event - event from textarea keyup.
   */


  function handleMessageUpdate(event) {
    const message = event.target.value.trim();
    cart.updateNote(message);
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init
  });
});